import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {QlService} from '../../../core/shared/ql/service/ql.service';
import {QlQueryBuilder} from '../../../core/shared/ql/builder/ql-query-builder';
import {ExternalPartyAssignmentDto} from '../../../core/cc/model/dto/entity/external-party-assignment.dto';
import {XxPersonExternalFlatTypeFieldDto} from '../model/dto/entity/xx-person-external-flat-type-field.dto';
import {XxPersonDto} from '../model/dto/entity/xx-person.dto';
import {MvsCrudService} from '../../../core/shared/service/crud/mvs-crud.service';
import {FilterCriteria} from '../../../core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from '../../../core/shared/dto/object/request/object-request-list';

/**
 * Flatten-row returned by {@link XxPersonExternalFlatQlService#loadPersonsWithExternal}.
 *
 * Core XxPerson fields are at the top level. Dynamic external side-car values
 * are stored as `ext_<typeFieldId>` on the same object (e.g. `ext_3`,
 * `ext_7`). Widget columns must be built dynamically from the requested
 * {@link typeFieldIds}.
 *
 * @ticket TM-1000001960
 */
export interface XxPersonFlatRow {
    /** QL result key: `p.id` */
    id: number;
    /** QL result key: `p.firstName` */
    firstName?: string;
    /** QL result key: `p.lastName` */
    lastName?: string;
    /** Dynamic side-car values keyed as `ext_<typeFieldId>` (e.g. `ext_3`) */
    [extKey: string]: unknown;
}

/**
 * Data-layer service for the Clean-Core External Side-Car flatten/QL widget
 * demo (TM-1000001960).
 *
 * Provides three methods the widget specialist needs:
 *
 * 1. {@link loadPersonsWithExternal} — runs the bounded-external QL flatten
 *    join and returns {@link XxPersonFlatRow} rows.
 * 2. {@link loadExternalPartyAssignments} — returns the list of available
 *    ExternalPartyAssignments for the assignment selector.
 * 3. {@link loadTypeFields} — returns the XxPersonExternalFlatTypeFields for
 *    a given assignment so the widget knows which ext_<id> columns exist.
 *
 * All HTTP calls go through the QL framework or standard MvsCrudService
 * endpoints — never direct HttpClient calls from this service.
 *
 * @ticket TM-1000001960
 */
@Injectable({providedIn: 'root'})
export class XxPersonExternalFlatQlService {

    constructor(private qlService: QlService) {}

    /**
     * Run the bounded-external flatten QL for XxPerson, VERSION-scoped (TM-1000001977).
     *
     * Issues a `view`-type QL query with a single `simple` pipe.
     * The root entity (`xx.XxPerson`, alias `p`) is joined to its external
     * side-car (`xx.XxPersonExternalFlat`, alias `ext`) via
     * {@link QlQueryBuilder.boundedExternalVersionJoin}.
     *
     * VERSION-AXIS SPLIT (TM-1000001977): the flatten read path now scopes by the
     * shared ExternalPartyVersion stream, NOT the per-target ExternalPartyAssignment.
     * The convenience factory binds `{ external_party_version_id: <versionId> }` into
     * the join `ON`, so multiple host assignments that share ONE version stream resolve
     * the SAME flattened side-car columns. The generic
     * {@link QlQueryBuilder.boundedExternalJoinGeneric} factory (key
     * `external_party_assignment_id`) remains available for un-migrated
     * ci/cw/pm consumers.
     *
     * The backend flattens the requested type-field ids into columns named
     * `p.ext_<typeFieldId>` in the `simple` pipe result. This method strips
     * the alias prefix and returns a normalized {@link XxPersonFlatRow}
     * array where:
     * - `id`        ← `p.id`
     * - `firstName` ← `p.firstName`
     * - `lastName`  ← `p.lastName`
     * - `ext_<id>`  ← `p.ext_<typeFieldId>` for every id in typeFieldIds
     *
     * @param versionId     The numeric ExternalPartyVersion ID to bind into the join
     *                      ON clause (passed to boundedExternalVersionJoin as
     *                      `external_party_version_id`).
     * @param typeFieldIds  The XxPersonExternalFlatTypeField IDs to flatten.
     *                      Each produces an `ext_<id>` column in the result row.
     * @returns Observable of normalized flat rows.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    loadPersonsWithExternal(
        versionId: number,
        typeFieldIds: number[]
    ): Observable<XxPersonFlatRow[]> {
        return this.qlService.builder()
            .query({
                name: 'personsWithExternal',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'xx.XxPerson',
                    as: 'p',
                    joins: [
                        QlQueryBuilder.boundedExternalVersionJoin(
                            'xx.XxPersonExternalFlat',
                            'ext',
                            versionId,
                            typeFieldIds
                        )
                    ]
                }
            })
            .pipeType('simple')
            .execute()
            .pipe(
                map(result => {
                    const rows: any[] = result.get('personsWithExternal');
                    return rows.map((row: any): XxPersonFlatRow => {
                        const flat: XxPersonFlatRow = {
                            id: row['p.id'] as number,
                            firstName: row['p.firstName'] as string | undefined,
                            lastName: row['p.lastName'] as string | undefined,
                        };
                        // Map dynamic ext columns: p.ext_<id> → ext_<id>
                        for (const typeFieldId of typeFieldIds) {
                            const sourceKey = `p.ext_${typeFieldId}`;
                            flat[`ext_${typeFieldId}`] = row[sourceKey];
                        }
                        return flat;
                    });
                })
            );
    }

    /**
     * Load all available ExternalPartyAssignments for the assignment selector.
     *
     * The widget selector calls this to populate the dropdown that lets the
     * user choose which external party's side-car schema to display.
     *
     * @returns Observable of ExternalPartyAssignment DTOs (id + name + protocol + version).
     *
     * @ticket TM-1000001960
     */
    loadExternalPartyAssignments(): Observable<ExternalPartyAssignmentDto[]> {
        return this.qlService.builder()
            .query({
                name: 'externalPartyAssignments',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cc.ExternalPartyAssignment',
                    as: 'e'
                }
            })
            .pipeType('object')
            .execute()
            .pipe(
                map(result => result.get('externalPartyAssignments') as ExternalPartyAssignmentDto[])
            );
    }

    /**
     * Load the XxPersonExternalFlatTypeFields for a given ExternalPartyVersion stream.
     *
     * The widget calls this after the user selects an assignment to discover
     * which `ext_<id>` columns it should request and render as table columns.
     *
     * VERSION-AXIS SPLIT (TM-1000001977): column discovery is now VERSION-scoped, in
     * lock-step with the value read path. `xx.XxPersonExternalFlatType` migrated its FK
     * from the per-target assignment to the shared `ExternalPartyVersion`
     * (`external_party_version_id`); the legacy `externalPartyAssignmentId` column no
     * longer exists. Two assignments sharing one version stream therefore discover the
     * SAME field set. Because the FE does not have a single cross-join from
     * version→type→typeField, this uses an inter-query dependency: Query A fetches the
     * type ids for the version, Query B fetches the fields for those types.
     *
     * @param versionId The ExternalPartyVersion ID to look up.
     * @returns Observable of type-field DTOs (id + name + fieldType).
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    loadTypeFields(versionId: number): Observable<XxPersonExternalFlatTypeFieldDto[]> {
        return this.qlService.builder()
            .query({
                name: 'flatTypes',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'xx.XxPersonExternalFlatType',
                    as: 'e'
                },
                filters: [
                    FilterCriteria.create('e.externalPartyVersionId', FilterCriteria.cOperatorEqual, versionId)
                ]
            })
            .query({
                name: 'typeFields',
                type: 'view',
                dependsOn: ['flatTypes'],
                start: {
                    type: 'entity',
                    name: 'xx.XxPersonExternalFlatTypeField',
                    as: 'e'
                },
                filters: [
                    FilterCriteria.create(
                        'e.typeId',
                        FilterCriteria.cOperatorIn,
                        '${query:flatTypes:e.id}'
                    )
                ]
            })
            .pipeType('object')
            .execute()
            .pipe(
                map(result => result.get('typeFields') as XxPersonExternalFlatTypeFieldDto[])
            );
    }

    /**
     * Convenience helper: load type fields and extract the numeric IDs
     * suitable for passing directly to {@link loadPersonsWithExternal}.
     *
     * @param versionId The ExternalPartyVersion ID (TM-1000001977).
     * @returns Observable of numeric type-field IDs.
     *
     * @ticket TM-1000001960
     * @ticket TM-1000001977
     */
    loadTypeFieldIds(versionId: number): Observable<number[]> {
        return this.loadTypeFields(versionId).pipe(
            map((fields: XxPersonExternalFlatTypeFieldDto[]) => fields.map(f => f.id))
        );
    }
}
